import {
  defaultPageSections,
  defaultSection,
  pageAnchorType,
  type PageId,
} from '@/features/storefront/sections/pages';
import { isAnchor, parseSections, type Section } from '@/features/storefront/sections/schema';
import type { Theme } from '@/features/storefront/themeSchema';

/**
 * A theme document, read and written one page at a time.
 *
 * Two shapes of document exist and both are current. Before pages, a theme
 * carried `sections`, and that array was the home page; now it carries `pages`,
 * a stack per page id. Every theme saved by every deploy before this one is the
 * first shape, and no migration runs over them - this module is the migration,
 * performed on read, every read, so an untouched theme keeps rendering what it
 * rendered and starts carrying `pages` the first time a merchant saves it.
 *
 * Nothing outside this file should reach for `theme.sections` or `theme.pages`.
 * That is the whole point: one place knows both shapes, and the builder, the
 * preview and the shopper's storefront all ask the same two questions - what is
 * on this page, and what does the document look like with this page changed.
 */

/** Where a theme's own per-page defaults come from. See `previewThemes/types.ts`. */
export type ThemePageDefaults = (page: PageId) => Section[] | undefined;

/**
 * The page as the merchant last left it, or as it has always looked.
 *
 * The order below is the order of authority: what was saved for this page,
 * then - for the home page only - the legacy array that *was* the home page,
 * then what this theme ships for the page, then the shop's own defaults. A
 * page therefore never renders empty because of a field that did not exist
 * when the document was written.
 */
export function pageSections(
  theme: Pick<Theme, 'sections' | 'pages'> | null | undefined,
  page: PageId,
  themeDefaults?: ThemePageDefaults,
): Section[] {
  const saved = theme?.pages?.[page];
  if (Array.isArray(saved) && saved.length > 0) {
    return withAnchor(page, parseSections(saved));
  }

  if (page === 'home') {
    const legacy = parseSections(theme?.sections);
    if (legacy.length > 0) return legacy;
  }

  const fromTheme = themeDefaults?.(page);
  if (fromTheme && fromTheme.length > 0) return withAnchor(page, fromTheme);

  return defaultPageSections(page);
}

/**
 * The document with one page replaced.
 *
 * The home page is written to both fields. `sections` is what a deploy older
 * than this one reads, what the API has always validated, and what a rollback
 * would fall back to - so keeping it in step costs one array and buys the
 * ability to roll back without blanking every shop's home page. Every other
 * page lives only in `pages`, because there was never anywhere else to put it.
 */
export function withPageSections<T extends Pick<Theme, 'sections' | 'pages'>>(
  theme: T,
  page: PageId,
  sections: Section[],
): T {
  const normalised = withAnchor(page, sections);
  const next = {
    ...theme,
    pages: { ...(theme.pages ?? {}), [page]: normalised as unknown as Record<string, unknown>[] },
  };
  return page === 'home'
    ? { ...next, sections: normalised as unknown as Record<string, unknown>[] }
    : next;
}

/**
 * The same list, guaranteed to carry its page's anchor exactly once.
 *
 * The builder will not delete or duplicate an anchor, so in practice this is
 * about documents the builder did not write: one hand-edited, one restored
 * from a deploy where the page had a different anchor, one generated. A cart
 * page with no cart in it is the failure worth defending against, and it is
 * repaired by putting the missing anchor back at the top rather than by
 * refusing to render the page.
 */
export function withAnchor(page: PageId, sections: Section[]): Section[] {
  const anchor = pageAnchorType(page);
  if (!anchor) return sections.filter((section) => !isAnchor(section));

  let seen = false;
  const kept = sections.filter((section) => {
    if (!isAnchor(section)) return true;
    if (section.type !== anchor || seen) return false;
    seen = true;
    return true;
  });

  return seen ? kept : [defaultSection(anchor), ...kept];
}

/** Whether this page has ever been arranged, for the customiser's "customised" mark. */
export function isPageCustomised(
  theme: Pick<Theme, 'sections' | 'pages'> | null | undefined,
  page: PageId,
): boolean {
  const saved = theme?.pages?.[page];
  if (Array.isArray(saved) && saved.length > 0) return true;
  return page === 'home' && parseSections(theme?.sections).length > 0;
}
